/**
 * Como uma questão se chama.
 *
 * Mora aqui pelo mesmo motivo de `modules.ts`: é texto de domínio que aparece
 * em mais de uma tela e nos dois aplicativos, e não pode divergir. Já estava
 * escrito em três lugares - cartão do aluno, tela da questão e listagem do
 * backoffice -, e três cópias de uma regra de nomenclatura acabam com a mesma
 * questão tendo nomes diferentes conforme a tela.
 */

/** As partes de uma prova discursiva, como a banca as divide. */
export type EssayKindKey = 'dissertation' | 'practical_piece' | 'essay_question';

export const TIPO_DISCURSIVA_LABEL: Record<EssayKindKey, string> = {
  dissertation: 'Dissertação',
  practical_piece: 'Peça prática',
  essay_question: 'Questão discursiva',
};

export const TIPOS_DISCURSIVOS = Object.keys(TIPO_DISCURSIVA_LABEL) as EssayKindKey[];

export interface IdentidadeDaQuestao {
  essayKind?: EssayKindKey | string | null;
  /** O título exato usado pela banca, quando existe: "II. Peça prática". */
  label?: string | null;
  /** O número na prova de origem. */
  position?: number | null;
}

/**
 * O nome da questão, na ordem de precedência do domínio.
 *
 * **O rótulo da banca ganha de tudo.** Quando ela nomeia a parte - "II. Peça
 * prática" -, esse é o nome pelo qual a questão é conhecida, e costuma já
 * trazer a numeração dela.
 *
 * **Sem rótulo, a discursiva leva tipo e número.** Só o tipo faria uma prova
 * com três questões discursivas mostrar três títulos iguais, e numa lista não
 * haveria como saber qual é qual.
 *
 * **Na objetiva, o número da prova.** É o que a banca usou e o que permite
 * achar a questão na prova original - por isso nunca o índice de uma busca,
 * que muda a cada filtro e não serve para procurar nem para conversar.
 *
 * Sem número registrado sobra o nome sem número: é falha de cadastro, e o lugar
 * de resolver é o backoffice, não um número inventado na tela.
 */
export function nomeDaQuestao(questao: IdentidadeDaQuestao | null | undefined): string {
  if (!questao) return 'Questão';
  if (questao.label) return questao.label;

  if (questao.essayKind) {
    const tipo = TIPO_DISCURSIVA_LABEL[questao.essayKind as EssayKindKey] ?? 'Questão';
    return [tipo, questao.position].filter(Boolean).join(' ');
  }

  return questao.position ? `Questão ${questao.position}` : 'Questão';
}
